import { useCallback, useRef, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import type { RootState } from '../../../redux/root-reducer';
import {
    clearCampaignHostCheckout,
    fetchCampaignHostCreationAccess,
    fetchCampaignHostMine,
    fetchCampaignHostPricing,
    purchaseCampaignHostLtd,
    subscribeCampaignHostMonthly,
    updateCampaignHostPaymentMethod,
} from '../../../redux/campaignHostSubscription/actions';
import {
    campaignHostPricingSelector,
    campaignHostSubscriptionSelector,
} from '../../../redux/campaignHostSubscription/selectors';
import type { ICampaignHostPackage } from '../../../types/global/campaignHostSubscription.types';
import { isActiveCampaignHostSubscription } from '../../../utils/campaignHostSubscriptionFormat';
import {
    buildCampaignHostPurchaseLtdBody,
    buildCampaignHostSubscribeBody,
    extractPaystackCheckoutResult,
    isPaystackCheckoutResult,
    redirectToPaystackCheckout,
    shouldUseFlutterwaveForCampaignHost,
    shouldUsePaystackForCampaignHost,
    storeCampaignHostReturnUrl,
} from '../../../utils/campaignHostSubscriptionRailUtils';
import { showToastMessage, useDispatchPromise } from '../../../utils/AppUtils';
import { useStripePromise } from '../../../hook/useStripePromise';

type PendingCheckout =
    | { mode: 'monthly' | 'ltd'; pkg: ICampaignHostPackage }
    | { mode: 'update_pm'; subscriptionId: string };

export interface UseCampaignHostCheckoutOptions {
    onSubscriptionComplete?: () => void;
}

export function useCampaignHostCheckout(options: UseCampaignHostCheckoutOptions = {}) {
    const { onSubscriptionComplete } = options;
    const dispatch = useDispatch();
    const store = useStore<RootState>();
    const dispatchPromise = useDispatchPromise();

    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const stripePromise = useStripePromise(null, { enabled: checkoutOpen });
    const [pending, setPending] = useState<PendingCheckout | null>(null);
    const [redirectProvider, setRedirectProvider] = useState<'paystack' | 'flutterwave' | null>(null);
    const pendingRef = useRef<PendingCheckout | null>(null);

    const setPendingCheckout = useCallback((value: PendingCheckout | null) => {
        pendingRef.current = value;
        setPending(value);
    }, []);

    const pollUntilActive = useCallback(async () => {
        for (let i = 0; i < 12; i++) {
            dispatch(fetchCampaignHostMine());
            await new Promise(resolve => setTimeout(resolve, i === 0 ? 800 : 2500));
            const sub = campaignHostSubscriptionSelector(store.getState());
            if (isActiveCampaignHostSubscription(sub)) {
                dispatch(fetchCampaignHostPricing());
                dispatch(fetchCampaignHostCreationAccess());
                dispatch(clearCampaignHostCheckout());
                showToastMessage('Subscription active', 'success');
                onSubscriptionComplete?.();
                return sub;
            }
        }
        throw new Error('Subscription activation timed out. Check Settings in a moment.');
    }, [dispatch, store, onSubscriptionComplete]);

    const confirmCardPayment = useCallback(
        async (clientSecret: string) => {
            const stripe = await stripePromise;
            if (!stripe) throw new Error('Stripe is not ready');
            const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret);
            if (error) {
                // Backend already confirmed the PaymentIntent — treat terminal states as success.
                const alreadyConfirmed =
                    error.code === 'payment_intent_unexpected_state' &&
                    (error.payment_intent?.status === 'succeeded' ||
                        error.payment_intent?.status === 'processing' ||
                        error.payment_intent?.status === 'requires_capture');
                if (!alreadyConfirmed) throw new Error(error.message ?? 'Payment failed');
                return;
            }
            const status = paymentIntent?.status;
            if (status && status !== 'succeeded' && status !== 'processing' && status !== 'requires_capture') {
                throw new Error('Payment was not completed');
            }
        },
        [stripePromise],
    );

    const startPaystackCheckout = useCallback(
        async (pkg: ICampaignHostPackage, mode: 'monthly' | 'ltd', provider: 'paystack' | 'flutterwave') => {
            setRedirectProvider(provider);
            try {
                const body =
                    mode === 'monthly'
                        ? buildCampaignHostSubscribeBody(pkg._id, { pkg })
                        : buildCampaignHostPurchaseLtdBody(pkg._id, { pkg });
                const result = (await dispatchPromise(
                    mode === 'monthly'
                        ? subscribeCampaignHostMonthly(body as { packageId: string })
                        : purchaseCampaignHostLtd(body),
                )) as unknown;

                const paystack = isPaystackCheckoutResult(result) ? result : extractPaystackCheckoutResult(result);
                if (!paystack?.authorization_url) {
                    throw new Error('Checkout could not be started.');
                }
                storeCampaignHostReturnUrl(window.location.pathname + window.location.search);
                redirectToPaystackCheckout(paystack);
            } catch (err: unknown) {
                setRedirectProvider(null);
                const message =
                    (err as { data?: { message?: string } })?.data?.message ??
                    (err as Error)?.message ??
                    'Checkout failed';
                showToastMessage(message, 'error');
                throw err;
            }
        },
        [dispatchPromise],
    );

    const beginCheckout = useCallback(
        async (pkg: ICampaignHostPackage, mode: 'monthly' | 'ltd') => {
            let pricing = campaignHostPricingSelector(store.getState());
            if (!pricing?.payment_rail && !pricing?.packages?.length) {
                dispatch(fetchCampaignHostPricing());
            }
            pricing = campaignHostPricingSelector(store.getState());

            if (shouldUseFlutterwaveForCampaignHost(pricing)) {
                setPendingCheckout({ pkg, mode });
                await startPaystackCheckout(pkg, mode, 'flutterwave');
                return;
            }
            if (shouldUsePaystackForCampaignHost(pricing)) {
                setPendingCheckout({ pkg, mode });
                await startPaystackCheckout(pkg, mode, 'paystack');
                return;
            }
            setPendingCheckout({ pkg, mode });
            setCheckoutOpen(true);
        },
        [dispatch, store, setPendingCheckout, startPaystackCheckout],
    );

    const beginUpdatePaymentMethod = useCallback(
        (subscriptionId: string) => {
            setPendingCheckout({ mode: 'update_pm', subscriptionId });
            setCheckoutOpen(true);
        },
        [setPendingCheckout],
    );

    const onPaymentMethodReady = useCallback(
        async (paymentMethodId: string) => {
            const checkout = pendingRef.current;
            if (!checkout) {
                throw new Error('Checkout session expired. Please select a plan again.');
            }

            if (checkout.mode === 'update_pm') {
                await dispatchPromise(
                    updateCampaignHostPaymentMethod({
                        subscriptionId: checkout.subscriptionId,
                        paymentMethodId,
                    }),
                );
                dispatch(fetchCampaignHostMine());
                setCheckoutOpen(false);
                setPendingCheckout(null);
                return;
            }

            const { pkg, mode } = checkout;

            if (mode === 'monthly') {
                const result = (await dispatchPromise(
                    subscribeCampaignHostMonthly({
                        packageId: pkg._id,
                        paymentMethodId,
                    }),
                )) as { clientSecret?: string | null; authorization_url?: string };

                if (result?.authorization_url) {
                    redirectToPaystackCheckout(extractPaystackCheckoutResult(result)!);
                    return;
                }
                if (result?.clientSecret) {
                    await confirmCardPayment(result.clientSecret);
                }
            } else {
                const result = (await dispatchPromise(
                    purchaseCampaignHostLtd({ packageId: pkg._id, paymentMethodId }),
                )) as { clientSecret?: string; authorization_url?: string };

                if (result?.authorization_url) {
                    redirectToPaystackCheckout(extractPaystackCheckoutResult(result)!);
                    return;
                }
                if (!result?.clientSecret) throw new Error('Missing payment confirmation');
                await confirmCardPayment(result.clientSecret);
            }

            await pollUntilActive();
            setCheckoutOpen(false);
            setPendingCheckout(null);
        },
        [dispatch, dispatchPromise, confirmCardPayment, pollUntilActive, setPendingCheckout],
    );

    const checkoutTitle =
        pending?.mode === 'update_pm'
            ? 'Update payment method'
            : pending?.mode === 'ltd'
            ? `Checkout — ${pending.pkg.name}`
            : `Subscribe — ${pending?.pkg?.name ?? 'Monthly Pro'}`;

    const pricing = campaignHostPricingSelector(store.getState());
    const usesPaystack = shouldUsePaystackForCampaignHost(pricing) || shouldUseFlutterwaveForCampaignHost(pricing);

    return {
        checkoutOpen,
        setCheckoutOpen,
        beginMonthly: (pkg: ICampaignHostPackage) => beginCheckout(pkg, 'monthly'),
        beginLtd: (pkg: ICampaignHostPackage) => beginCheckout(pkg, 'ltd'),
        beginUpdatePaymentMethod,
        onPaymentMethodReady,
        checkoutTitle,
        usesPaystack,
        paystackRedirecting: redirectProvider !== null,
        redirectProvider,
    };
}
